import { useEffect, useRef } from "react";
import { celebrationLink } from "../../lib/celebrationLink";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../../api/misc";
import qplusIcon from "../../assets/qplus-icon.png";
import { PUSH_FLAG, resyncPush } from "../../lib/push";

const SUPPORTS_NOTIFICATIONS = typeof window !== "undefined" && "Notification" in window;

/**
 * No UI of its own — pops a real OS-level desktop notification for each
 * newly-arrived notification while the tab is open, on top of the existing
 * in-app bell (NotificationBell.tsx). Shares that same 15s poll via
 * useNotifications() rather than adding a second one.
 */
export const DesktopNotifications: React.FC = () => {
  const { data } = useNotifications();
  const navigate = useNavigate();
  const seenIds = useRef<Set<string> | null>(null);

  // request 1069: keep this device's push registration with whoever is signed in
  useEffect(() => {
    void resyncPush();
  }, []);

  useEffect(() => {
    if (!SUPPORTS_NOTIFICATIONS || Notification.permission !== "default") return;
    Notification.requestPermission();
  }, []);

  useEffect(() => {
    if (!SUPPORTS_NOTIFICATIONS) return;
    const items: any[] = data?.data?.items ?? [];

    if (!seenIds.current) {
      // First load after mount is the existing backlog, not a fresh
      // arrival — record it as the baseline without popping anything.
      seenIds.current = new Set(items.map((n) => n.id));
      return;
    }

    for (const n of items) {
      if (seenIds.current.has(n.id)) continue;
      seenIds.current.add(n.id);
      if (n.isRead || Notification.permission !== "granted") continue;
      // this device gets real push notifications (request 1069) — don't show the same one twice
      let pushOn = false;
      try {
        pushOn = localStorage.getItem(PUSH_FLAG) === "1";
      } catch {
        pushOn = false;
      }
      if (pushOn) continue;

      // "Notification" existing on window doesn't guarantee the constructor
      // itself is usable — most mobile browsers report support and grant
      // permission, but throw "Illegal constructor" on `new Notification()`,
      // requiring ServiceWorkerRegistration.showNotification() instead. That
      // throw happens inside this effect on every poll tick once there's an
      // unread item, which (with no error boundary at the time) took down
      // the whole app. Skip silently on platforms where it isn't supported,
      // rather than crash — this is a nice-to-have popup, not core behavior.
      try {
        const popup = new Notification(n.title, {
          body: new Date(n.createdAt).toLocaleString(),
          icon: qplusIcon,
          tag: n.id,
        });
        popup.onclick = () => {
          window.focus();
          if (n.taskId) navigate(`/workflow/boards/${n.boardId ?? ""}?task=${n.taskId}`);
          else if (n.link) navigate(n.link);
          // older ticket notifications have no link — open the ticket named in the title
          else if (/TKT-\d+/.test(n.title ?? "")) navigate(`/tickets?ticket=${/TKT-\d+/.exec(n.title)![0]}`);
          else if (/final settlement/i.test(n.title ?? "")) navigate("/finance/accounts/settlements");
          // request 1165: birthday / anniversary reminders open Celebrations for that month
          else if (celebrationLink(n.title, n.createdAt)) navigate(celebrationLink(n.title, n.createdAt)!);
          else if (n.boardId) navigate(`/workflow/boards/${n.boardId}`);
          popup.close();
        };
      } catch (err) {
        console.warn("Desktop notification popup not supported on this platform:", err);
      }
    }
  }, [data, navigate]);

  return null;
};
